const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Shrinks an image file so its longest edge is at most `maxEdge`, re-encoded as
 * JPEG. Returns the original file untouched when it is already small enough and
 * in a format the API accepts, or when anything in the canvas path fails.
 *
 * Drawn from an `<img>` rather than `createImageBitmap`: browsers apply an
 * image's EXIF orientation when it is loaded into an `<img>`, while
 * `createImageBitmap` ignores it unless explicitly told otherwise. That matters
 * here because the canvas output carries no EXIF of its own, so whatever
 * rotation is baked in at this point is final and nothing downstream can
 * correct it.
 *
 * Re-encoding also normalizes formats the browser can decode but our API
 * rejects, which is how an iPhone's HEIC becomes a JPEG.
 */
export async function downscaleImage(file: File, maxEdge = 1024, quality = 0.85): Promise<File> {
	if (!file.type.startsWith('image/')) {
		return file;
	}

	const objectUrl = URL.createObjectURL(file);
	try {
		const image = await loadImage(objectUrl);
		const longestEdge = Math.max(image.naturalWidth, image.naturalHeight);
		if (longestEdge <= maxEdge && ALLOWED_TYPES.includes(file.type)) {
			return file;
		}

		const scale = Math.min(1, maxEdge / longestEdge);
		const canvas = document.createElement('canvas');
		canvas.width = Math.round(image.naturalWidth * scale);
		canvas.height = Math.round(image.naturalHeight * scale);

		const context = canvas.getContext('2d');
		if (!context) {
			return file;
		}
		context.imageSmoothingQuality = 'high';
		context.drawImage(image, 0, 0, canvas.width, canvas.height);

		const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
		// Older iOS blanks the canvas for very large images rather than failing
		// outright, which surfaces as a suspiciously tiny blob.
		if (!blob || blob.size < 1024) {
			return file;
		}

		return new File([blob], `${stripExtension(file.name)}.jpg`, { type: 'image/jpeg' });
	} catch {
		return file;
	} finally {
		URL.revokeObjectURL(objectUrl);
	}
}

/**
 * Turns an image our API rejects (HEIC, GIF, SVG, ...) into a PNG on white,
 * its longest edge at most `maxEdge`. One the API takes is returned untouched:
 * this is for chat attachments, which the model gets as a PocketBase thumb
 * anyway, so only the format needs fixing. PNG rather than JPEG because what
 * lands here is often line art whose text JPEG would blur, and white because
 * transparency reaches the model as black. Falls back to the original when the
 * browser can't decode it, which the backend then rejects with a reason.
 */
export async function toAcceptedImage(file: File, maxEdge = 1024): Promise<File> {
	if (!file.type.startsWith('image/') || ALLOWED_TYPES.includes(file.type)) {
		return file;
	}

	let objectUrl = '';
	try {
		const source = file.type === 'image/svg+xml' ? await sizedSvg(file, maxEdge) : file;
		objectUrl = URL.createObjectURL(source);
		const image = await loadImage(objectUrl);
		const scale = Math.min(1, maxEdge / Math.max(image.naturalWidth, image.naturalHeight));
		const canvas = document.createElement('canvas');
		canvas.width = Math.round(image.naturalWidth * scale);
		canvas.height = Math.round(image.naturalHeight * scale);

		const context = canvas.getContext('2d');
		if (!context) {
			return file;
		}
		context.fillStyle = 'white';
		context.fillRect(0, 0, canvas.width, canvas.height);
		context.imageSmoothingQuality = 'high';
		context.drawImage(image, 0, 0, canvas.width, canvas.height);

		const blob = await canvasToBlob(canvas, 'image/png');
		if (!blob) {
			return file;
		}
		return new File([blob], `${stripExtension(file.name)}.png`, { type: 'image/png' });
	} catch {
		return file;
	} finally {
		if (objectUrl) {
			URL.revokeObjectURL(objectUrl);
		}
	}
}

/**
 * The SVG with its width and height set so its longest edge is `maxEdge`: an
 * SVG declares any size or none (a 24px icon, or only a viewBox), and browsers
 * disagree on how big to draw one without. A viewBox is added from the
 * declared size when missing, so the drawing scales with the new size.
 */
async function sizedSvg(file: File, maxEdge: number): Promise<Blob> {
	const svg = new DOMParser().parseFromString(await file.text(), 'image/svg+xml').documentElement;
	// Only a size in px converts to the viewBox's user units.
	const [declaredWidth, declaredHeight] = ['width', 'height'].map((name) => {
		const value = svg.getAttribute(name)?.trim() ?? '';
		return /^\d*\.?\d+(px)?$/.test(value) ? parseFloat(value) : 0;
	});
	if (!svg.hasAttribute('viewBox') && declaredWidth > 0 && declaredHeight > 0) {
		svg.setAttribute('viewBox', `0 0 ${declaredWidth} ${declaredHeight}`);
	}
	const [, , boxWidth, boxHeight] = (svg.getAttribute('viewBox') ?? '')
		.trim()
		.split(/[\s,]+/)
		.map(Number);
	// Without a viewBox or a size, the drawing is unscaled on a square.
	const aspect = boxWidth > 0 && boxHeight > 0 ? boxWidth / boxHeight : 1;
	svg.setAttribute('width', String(Math.round(aspect >= 1 ? maxEdge : maxEdge * aspect)));
	svg.setAttribute('height', String(Math.round(aspect >= 1 ? maxEdge / aspect : maxEdge)));
	return new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml' });
}

function loadImage(src: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const image = new Image();
		image.onload = () => resolve(image);
		image.onerror = () => reject(new Error('Image could not be decoded'));
		image.src = src;
	});
}

function canvasToBlob(
	canvas: HTMLCanvasElement,
	type: string,
	quality?: number
): Promise<Blob | null> {
	return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

function stripExtension(filename: string) {
	return filename.replace(/\.[^./\\]+$/, '') || 'photo';
}
